<template>
  <div class="search-col">
    <form
      @submit.prevent="search"
      class="search-form main-search"
      autocomplete="off"
    >
      <!-- Input for search -->
      <input
        type="search"
        id="searchField"
        name="searchField"
        placeholder="Search All Transcripts"
        autocomplete="off"
        v-model="searchField"
        @keydown.escape="clearSearch"
        aria-label="Search all transcripts"
      />
      <!-- Search icon and its animation -->
      <button type="submit" class="search-icon icon-search"></button>
      <a href="#" class="search_close-icon icon-close"></a>
      <!-- Search results dropdown -->
      <div class="search-drop" v-click-outside="onClickOutside">
        <div class="result-filter">
          <div class="result-count" v-if="resultsCount > 0">
            {{ resultsCount }} Results
          </div>
          <span v-if="isLoading" class="icon-setting"></span>
          <!-- Display spinner when loading -->
        </div>
        <div class="result_block">
          <!--
            A failed search is not "no results": the tips below would blame
            the user's spelling for a request that never succeeded.
          -->
          <div
            v-if="searchError"
            class="no-result_block search-error_block"
            role="alert"
          >
            <h2 class="h3">Search unavailable</h2>
            <p class="margin-0">
              Search isn't available right now. Please try again.
            </p>
            <button type="button" class="search-error_retry" @click="search">
              Try again
            </button>
          </div>
          <!-- Display when there's no results -->
          <div class="no-result_block" v-else-if="resultsCount == 0">
            <h2 class="h3">No Results</h2>
            <p class="margin-0">
              We couldn’t find any conversations matching your search. You may
              want to try…
            </p>
            <ul>
              <li>Using different keywords</li>
              <li>Checking for typos</li>
              <li>Adjusting your filters</li>
            </ul>
            <!--
              Search cannot tell "no match" from "nothing to search yet", and the
              tips above are actively misleading in the second case — the problem
              is not the user's spelling. Offer the real fix as well.
            -->
            <p class="no-result_import">
              Nothing imported yet?
              <button
                type="button"
                class="no-result_import-btn"
                @click="openImport"
              >
                Import a file
              </button>
            </p>
          </div>
          <!-- Display results -->
          <a
            :href="`/${workspaceStore.activeWorkspaceUUID}/transcription/${result.audio_file_uuid}`"
            class="result-card"
            v-for="result in searchResults"
            :key="result.audiofile_uuid"
          >
            <h2>{{ result.user_defined_name || result.original_file_name }}</h2>
            <div class="result-date">
              <span class="date-icon icon-calendar"></span>
              <span class="date-txt">{{ formatDate(result.created_at) }}</span>
            </div>
            <p v-html="result?.highlight?.transcription_text?.snippet"></p>
          </a>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { search } from "@/services/useSearch.service";
import { useWorkSpaceStore } from "@/stores/workspace-store";
import { useAppStore } from "@/stores/app-store";
import { mapStores } from "pinia";
import moment from "moment";
import { analytics } from "@/services/analytics.service";
import {
  ONBOARDING_STEPS,
  useOnboardingChecklistStore,
} from "@/stores/onboarding-checklist-store";

const clickOutsideDirective = {
  beforeMount(el, binding) {
    el.clickOutsideEvent = function (event) {
      // Get the reference to the search form elements
      //const searchForm = el.querySelector('.search-form');
      const searchForm = el.parentElement;
      // Check if the event target is part of the excluded elements
      if (!searchForm.contains(event.target)) {
        binding.value(event);
      }
    };
    document.body.addEventListener("click", el.clickOutsideEvent);
    document.body.addEventListener("pointerdown", el.clickOutsideEvent);
  },
  unmounted(el) {
    document.body.removeEventListener("click", el.clickOutsideEvent);
    document.body.removeEventListener("pointerdown", el.clickOutsideEvent);
  },
};

export default {
  name: "Search",
  directives: {
    clickOutside: clickOutsideDirective,
  },
  computed: {
    ...mapStores(useWorkSpaceStore, useAppStore, useOnboardingChecklistStore),
    resultsCount: function () {
      return this.searchResults?.length > 0 ? this.searchResults.length : 0;
    },
  },
  data() {
    return {
      searchField: "",
      noResults: false,
      searchResults: [],
      isLoading: false, // New data property for loading indicator
      // The last search failed: a different state from "no results".
      searchError: false,
      // Bumped for every search, and whenever it's dismissed. Only the
      // request holding the latest id may update the drop-down.
      latestSearchId: 0,
    };
  },
  beforeUnmount() {
    // A search that settles after the page is gone must not reopen the
    // drop-down (search-active is a class on <body>).
    this.latestSearchId += 1;
  },
  methods: {
    /**
     * Plain click, so Enter and Space work: v-click-outside ignores clicks
     * inside .search-drop, so the drop-down survives without a mousedown hack.
     */
    openImport() {
      this.appStore.openImportPopup();
    },
    async search() {
      if (!this.searchField.trim()) {
        this.clearSearch();
        return;
      }
      // Capture what's being searched now: the input (and workspace) can
      // change before the request settles.
      const query = this.searchField;
      const workspaceUUID = this.workspaceStore.activeWorkspaceUUID;
      // A newer search, or dismissing this one, supersedes it. A superseded
      // request must not touch results, error, spinner or analytics.
      const requestId = ++this.latestSearchId;
      const isLatest = () => requestId === this.latestSearchId;

      this.isLoading = true; // Start loading animation
      this.searchError = false;

      // Track search submitted
      analytics.searchSubmitted({
        query,
        query_length: query.length,
      });
      // Home checklist step. No-ops after the first report.
      this.onboardingChecklistStore.markStep(ONBOARDING_STEPS.SEARCH);

      try {
        const response = await search(workspaceUUID, query);
        if (!isLatest()) return;
        // The search helper resolves a failed request as { data: null, error }
        // rather than throwing; treat it like a thrown error.
        if (response?.error) {
          throw new Error(response.error.message || "Search request failed");
        }
        this.searchResults = response?.data || [];

        // Track search results
        if (this.searchResults.length > 0) {
          analytics.searchResultsDisplayed({
            query,
            results_count: this.searchResults.length,
          });
        } else {
          analytics.searchNoResults({ query });
        }
      } catch (error) {
        if (!isLatest()) return;
        console.error("[Search] Search failed:", error);
        this.searchResults = [];
        this.searchError = true;
      } finally {
        // Also covers a token failure, which throws before any request.
        if (isLatest()) {
          this.isLoading = false; // End loading animation
          document.body.classList.add("search-active");
        }
      }
    },
    /** Supersede any search still in flight so it can't touch the drop-down. */
    abandonPendingSearch() {
      this.latestSearchId += 1;
      this.isLoading = false;
    },
    clearSearch() {
      // A search still in flight must not reopen the drop-down or bring its
      // results back after the user dismissed it.
      this.abandonPendingSearch();
      const previousQuery = this.searchField;
      this.searchField = "";
      document.body.classList.remove("search-active");
      if (previousQuery) {
        analytics.searchCleared({ query: previousQuery });
      }
    },
    onClickOutside() {
      //console.log("Clicked outside.");
      this.clearSearch();
    },
    formatDate(value) {
      return moment.unix(value).format("MMMM Do, YYYY");
    },
    formatTime(value) {
      return moment.unix(value).format("h:mm A");
    },
  },
};
</script>

<!-- Add styles for loading animation -->
<style scoped>
.icon-setting {
  /* Add styles for the spinner */
  /* For example, if you're using a rotating animation: */
  animation: rotate 1s linear infinite;
  margin-left: 5px;
  /* Adjust spacing as per your design */
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.no-result_import {
  margin: 12px 0 0;
  font-size: 13px;
  color: #5a6b75;
}

.no-result_import-btn,
.search-error_retry {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: #e15511;
  cursor: pointer;
  text-decoration: underline;
}

.search-error_retry {
  margin-top: 12px;
}
</style>
